/**
 * CORE-1725 — кто зритель в обсуждении события и что ему показать внизу
 * панели (LiveCommentsPanel).
 *
 * Владелец запланированного эфира видел в предбаннике «Хотите Обсудить?
 * Присоединяйтесь :)» и окно «Вы подпишетесь на мастера…» — то есть
 * предложение подписаться на самого себя; на проде согласие так и создавало
 * подписку на себя. Причин было две, и обе здесь:
 *
 *   1. Членство спрашивалось анонимно (`getMasterCircle` без токена), и сервер
 *      честно отвечал `isMember: null` — «не знаю, кто вы». Клиент читал это
 *      как «не участник», и CTA видел каждый: владелец, подписчик, все.
 *   2. `isOwner` у хука был зашит в `false`.
 *
 * Владелец определяется по ID, выданным сервером: свой `masterProfileId` из
 * `/api/auth/me` против `masterProfileId` круга, к которому привязано
 * обсуждение (у комнаты это `hostMasterProfileId` — профиль АВТОРА сессии,
 * CORE-1328). Не по имени, не по параметру адреса и не сравнением userId с
 * masterProfileId — это разные пространства идентификаторов.
 *
 * Состояния перечислены полностью, и «ещё не знаем» — отдельное: пока ответа о
 * членстве нет, предложение подписаться не показывается никому. Иначе на
 * медленной сети подписчик видел бы мигающую CTA.
 */

/** Кем зритель приходится обсуждению. */
export type DiscussionMembership =
  /** Ответа о членстве ещё нет (или он «не знаю»). */
  | 'unknown'
  /** Хозяин круга: пишет без подписки, предложение подписаться ему не положено. */
  | 'owner'
  /** Участник круга (подписан на мастера). */
  | 'member'
  /** Сервер ответил: не участник. Только здесь уместна CTA подписки. */
  | 'not_member';

export interface DiscussionMembershipInput {
  /** `masterProfileId` круга, к которому привязано обсуждение. */
  masterProfileId: number | null | undefined;
  /** Свой `masterProfileId` зрителя из `/api/auth/me` (null у не-мастера). */
  viewerMasterProfileId: number | null | undefined;
  /**
   * `MasterCircleSummary.isMember` из запроса С токеном. `null`/`undefined` —
   * ответа нет, запрос упал или сервер не узнал вызывающего.
   */
  isMember: boolean | null | undefined;
}

const isRealId = (id: number | null | undefined): id is number =>
  typeof id === 'number' && Number.isFinite(id) && id > 0;

/** Владелец ли зритель этого круга — только по двум серверным ID. */
export function isDiscussionOwner(
  masterProfileId: number | null | undefined,
  viewerMasterProfileId: number | null | undefined,
): boolean {
  return isRealId(masterProfileId) && isRealId(viewerMasterProfileId) && masterProfileId === viewerMasterProfileId;
}

export function resolveDiscussionMembership(input: DiscussionMembershipInput): DiscussionMembership {
  // Владелец — раньше ответа о членстве: своё обсуждение доступно ему и до
  // ответа, и при упавшем запросе, и при самоподписке, оставшейся с прошлого.
  if (isDiscussionOwner(input.masterProfileId, input.viewerMasterProfileId)) return 'owner';
  if (input.isMember === true) return 'member';
  if (input.isMember === false) return 'not_member';
  return 'unknown';
}

/** Может ли зритель писать в обсуждение. */
export const canWriteToDiscussion = (membership: DiscussionMembership): boolean =>
  membership === 'owner' || membership === 'member';

/**
 * CORE-620 — можно ли отправить сейчас: права и готовность вместе. Без карточки
 * ответу не к чему крепиться, какими бы ни были права.
 */
export const canPostToDiscussion = (
  membership: DiscussionMembership,
  readiness: DiscussionReadiness,
): boolean => readiness === 'ready' && canWriteToDiscussion(membership);

/**
 * CORE-620 (актуализация 27.10) — готово ли само обсуждение: нашлась ли карточка
 * события, к которой крепятся ответы.
 *
 * Права (CORE-1725) отвечают на вопрос «можно ли мне писать», готовность — «есть
 * ли куда». Раньше второй вопрос не задавался: хук ставил «готово», даже не
 * найдя карточки, владелец и подписчик видели поле ввода, а отправка падала с
 * «Thread not ready».
 */
export type DiscussionReadiness =
  /** Карточку ещё ищем. */
  | 'loading'
  /** Карточка найдена — отвечать есть куда. */
  | 'ready'
  /** Карточки нет: не заведена (CORE-1764, CORE-1283) или не найдена. */
  | 'unavailable'
  /**
   * Канал не открылся. У неучастника это обычный путь: канал круга закрыт до
   * подписки, и тогда уместно предложение подписаться, а не «недоступно».
   */
  | 'error';

/** Что стоит внизу панели обсуждения. */
export type DiscussionFooter =
  /** Эфир завершён — обсуждение только для чтения. */
  | 'frozen'
  /** Гость — «Войдите, чтобы написать». */
  | 'signin'
  /** Поле ввода. */
  | 'composer'
  /** «Хотите Обсудить? Присоединяйтесь :)» → окно подписки на мастера. */
  | 'join'
  /** Членство или готовность ещё не известны — ни поля, ни предложения. */
  | 'pending'
  /** Обсуждения нет — объяснение вместо поля ввода и предложения подписаться. */
  | 'unavailable';

export function resolveDiscussionFooter(input: {
  isFrozen: boolean;
  isAuthenticated: boolean;
  membership: DiscussionMembership;
  /** Без значения — как раньше: обсуждение считается готовым. */
  readiness?: DiscussionReadiness;
}): DiscussionFooter {
  if (input.isFrozen) return 'frozen';
  // Обсуждения нет — ни поля, ни предложения подписаться, ни предложения
  // войти: подписываться или входить ради того, чего нет, бессмысленно.
  if (input.readiness === 'unavailable') return 'unavailable';
  if (!input.isAuthenticated) return 'signin';
  // Неучастнику канал круга закрыт до подписки, карточку ему не найти — путь
  // в обсуждение один: подписаться. Поэтому его членство решает раньше.
  if (input.membership === 'not_member') return 'join';
  if (input.readiness === 'loading') return 'pending';
  if (input.readiness === 'error') return 'unavailable';
  switch (input.membership) {
    case 'owner':
    case 'member':
      return 'composer';
    case 'unknown':
      return 'pending';
  }
}

/** Код отказа сервера на подписку мастера на самого себя (CORE-1725). */
export const SELF_SUBSCRIPTION_REFUSED_CODE = 'cannot_subscribe_to_self';

/** Отказ «нельзя подписаться на себя» — а не сбой сети или сервера. */
export function isSelfSubscriptionRefusal(err: unknown): boolean {
  const code = (err as { code?: unknown } | null | undefined)?.code;
  return typeof code === 'string' && code === SELF_SUBSCRIPTION_REFUSED_CODE;
}
